// Schedule tab — 3-pane layout (list / editor / preview), mirroring the
// Tasks tab. A Work Template can carry multiple schedules (e.g. a calendar
// trigger AND a meter-threshold trigger on the same template) — the left
// pane lists them, the middle pane edits the selected one, and the right
// pane shows its preview + forecast.

import { useContext, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import InventoryAssetTree from '@atom/components/common/inventoryAssetTree/InventoryAssetTree';
import WorkTemplateContext from '@atom/components/workTemplate/WorkTemplateContext';
import {
  Autocomplete,
  Button,
  Icon,
  IconButton,
  Modal,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
} from '@atom/mui';
import type { InventorySchemaItem } from '@atom/types/inventory';
import {
  getTemplateSchedules,
  type ScheduleTrigger,
  summarizeSchedule,
  type WorkTemplateSchedule,
} from '@atom/types/workSchedule';
import fonts from '@atom/styles/fonts';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import SchedulePreview from './SchedulePreview';
import TriggerBuilder from './TriggerBuilder';

import classes from './schedule.module.css';

const DEFAULT_TRIGGER: ScheduleTrigger = {
  kind: 'CALENDAR',
  cadence: 'MONTHLY',
  anchorDate: new Date().toISOString().slice(0, 10),
};

const newScheduleDefaults = (): Omit<WorkTemplateSchedule, 'id'> => ({
  active: true,
  targetCategoryId: '',
  trigger: { ...DEFAULT_TRIGGER },
  leadTimeDays: 7,
  supersedes: [],
  supersedesWindowDays: 7,
});

const ScheduleTab = () => {
  const { workTemplate, refetch } = useContext(WorkTemplateContext);

  // The list of schedules currently on the template. We keep a local copy so
  // we can render optimistically while a save is in flight; on refetch the
  // server copy wins.
  const [schedules, setSchedules] = useState<WorkTemplateSchedule[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Editor draft for the selected schedule. Mutated by the form, written
  // back via PUT on Save.
  const [draft, setDraft] = useState<WorkTemplateSchedule | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Supporting lookups for the editor.
  const [schemas, setSchemas] = useState<InventorySchemaItem[]>([]);
  const [categories, setCategories] = useState<
    Array<{ id: string; name: string; path?: string; schemaId?: string; colorId?: number }>
  >([]);
  const [allTemplates, setAllTemplates] = useState<
    Array<{ id: string; name: string; schedules?: WorkTemplateSchedule[]; schedule?: WorkTemplateSchedule }>
  >([]);

  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPickerDraft, setFolderPickerDraft] = useState<string>('');

  // Collapsible section state (expanded by default — matches Tasks Forms /
  // Asset Type defaults).
  const [triggerOpen, setTriggerOpen] = useState(true);
  const [coversOpen, setCoversOpen] = useState(true);

  // Inline-editable schedule title (TaskName pattern).
  const [titleEdit, setTitleEdit] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  useEffect(() => {
    setTitleDraft(draft?.name ?? '');
    setTitleEdit(false);
  }, [draft?.id]);

  // Sync local schedules list from the template on mount / when the template
  // changes upstream.
  useEffect(() => {
    if (!workTemplate?.id) return;
    const list = getTemplateSchedules(workTemplate as any);
    setSchedules(list);
    // Auto-select first schedule if none chosen, or keep current selection
    // if it still exists.
    setSelectedId(prev => {
      if (prev && list.some(s => s.id === prev)) return prev;
      return list[0]?.id ?? null;
    });
  }, [workTemplate?.id, (workTemplate as any)?.schedules, (workTemplate as any)?.schedule]);

  // When the selected id changes, sync the draft from the corresponding
  // schedule.
  useEffect(() => {
    const sel = schedules.find(s => s.id === selectedId) ?? null;
    setDraft(sel ? { ...sel } : null);
  }, [selectedId, schedules]);

  // Load schemas + categories + all templates once.
  useEffect(() => {
    fetch('/api/v1/schemas')
      .then(r => r.json())
      .then((s: any) => setSchemas(Array.isArray(s) ? s : []));
    fetch('/api/v1/inventory/categories/tree?includeAssetInfo=true')
      .then(r => r.json())
      .then((tree: any) => {
        const flat: typeof categories = [];
        const walk = (node: any) => {
          if (!node) return;
          flat.push({
            id: node.id,
            name: node.name,
            path: node.path,
            schemaId: node.schemaId,
            colorId: node.colorId,
          });
          (node.categories ?? []).forEach(walk);
        };
        walk(tree);
        setCategories(flat);
      });
    fetch('/api/v1/workTemplates?limit=200')
      .then(r => r.json())
      .then((arr: any) => setAllTemplates(Array.isArray(arr) ? arr : []));
  }, []);

  const targetCategory = useMemo(
    () => categories.find(c => c.id === draft?.targetCategoryId),
    [categories, draft?.targetCategoryId],
  );

  // Strip the leading "inventory/" prefix so paths read more like breadcrumbs.
  const formatPath = (raw?: string): string =>
    (raw ?? '').replace(/^inventory\/?/i, '');

  const folderColor = (cat?: typeof targetCategory): string | undefined =>
    cat?.schemaId
      ? schemaUtilities.getSchemaColorFromColorId(cat.colorId)
      : undefined;

  const schemaForTarget = useMemo(() => {
    const schemaId = targetCategory?.schemaId ?? workTemplate?.schemaId;
    if (!schemaId) return undefined;
    return schemas.find(s => s.id === schemaId);
  }, [schemas, targetCategory?.schemaId, workTemplate?.schemaId]);

  // Sibling templates that could be covered by this schedule.
  const otherTemplates = useMemo(
    () =>
      allTemplates
        .filter(t => t.id !== workTemplate?.id)
        .filter(t => getTemplateSchedules(t).some(s => s.active)),
    [allTemplates, workTemplate?.id],
  );

  const supersededByNames = useMemo(() => {
    if (!workTemplate?.id) return [];
    return allTemplates
      .filter(t =>
        getTemplateSchedules(t).some(s =>
          (s.supersedes ?? []).includes(workTemplate.id),
        ),
      )
      .map(t => t.name);
  }, [allTemplates, workTemplate?.id]);

  const patchDraft = (updates: Partial<WorkTemplateSchedule>) => {
    setDraft(d => (d ? { ...d, ...updates } : d));
  };

  // Create a new schedule on the server, then select it.
  const handleAdd = async () => {
    if (!workTemplate?.id) return;
    const seed = newScheduleDefaults();
    const res = await fetch(
      `/api/v1/work-templates/${workTemplate.id}/schedules`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(seed),
      },
    );
    if (!res.ok) {
      Snackbar.error({ message: 'Failed to add schedule' });
      return;
    }
    const created = (await res.json()) as WorkTemplateSchedule;
    setSchedules(prev => [...prev, created]);
    setSelectedId(created.id ?? null);
    refetch?.();
  };

  const handleSave = async () => {
    if (!workTemplate?.id || !draft?.id) return;
    const res = await fetch(
      `/api/v1/work-templates/${workTemplate.id}/schedules/${draft.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      },
    );
    if (!res.ok) {
      Snackbar.error({ message: 'Failed to save schedule' });
      return;
    }
    const body = await res.json().catch(() => ({}));
    const gen = body?.generated ?? 0;
    Snackbar.info({
      message:
        gen > 0
          ? `Schedule saved — ${gen} work order${gen === 1 ? '' : 's'} generated`
          : 'Schedule saved',
    });
    setSchedules(prev =>
      prev.map(s => (s.id === draft.id ? { ...draft } : s)),
    );
    setRefreshToken(t => t + 1);
    refetch?.();
    window.dispatchEvent(new CustomEvent('notifications:refresh'));
  };

  const handleDelete = async () => {
    if (!workTemplate?.id || !draft?.id) return;
    const ok = window.confirm('Delete this schedule? This cannot be undone.');
    if (!ok) return;
    await fetch(
      `/api/v1/work-templates/${workTemplate.id}/schedules/${draft.id}`,
      { method: 'DELETE' },
    );
    Snackbar.info({ message: 'Schedule deleted' });
    setSchedules(prev => prev.filter(s => s.id !== draft.id));
    setSelectedId(prev => {
      const remaining = schedules.filter(s => s.id !== draft.id);
      return remaining[0]?.id ?? null;
    });
    refetch?.();
  };

  const handleGenerate = async () => {
    if (!workTemplate?.id || !draft?.id) return;
    const res = await fetch(
      `/api/v1/work-templates/${workTemplate.id}/schedules/${draft.id}/generate`,
      { method: 'POST' },
    );
    if (!res.ok) {
      Snackbar.error({ message: 'Generation failed' });
      return;
    }
    const body = await res.json();
    const count = body.generated?.length ?? 0;
    Snackbar.info({
      message:
        count === 0
          ? 'No assets currently match the schedule trigger'
          : `Generated ${count} work order${count === 1 ? '' : 's'}`,
    });
    setRefreshToken(t => t + 1);
    window.dispatchEvent(new CustomEvent('notifications:refresh'));
  };

  if (!workTemplate?.id) return null;

  const renderListItem = (s: WorkTemplateSchedule) => {
    const isSelected = s.id === selectedId;
    const name = s.name ?? summarizeSchedule(s);
    const cat = categories.find(c => c.id === s.targetCategoryId);
    const targetName = cat?.name ?? 'No folder';
    const color = folderColor(cat);
    return (
      <div
        key={s.id}
        className={clsx(
          classes.listItem,
          isSelected && classes.listItemSelected,
        )}
        onClick={() => s.id && setSelectedId(s.id)}
      >
        <span
          className={clsx(
            classes.activeDot,
            s.active ? classes.activeDotOn : classes.activeDotOff,
          )}
          title={s.active ? 'Active' : 'Inactive'}
        />
        <div className={classes.listItemBody}>
          <div className={classes.listItemName}>{name}</div>
          <div className={classes.listItemSubtitle}>
            <Icon
              className={classes.subtitleIcon}
              style={{ color: color ?? 'var(--gray)' }}
            >
              folder
            </Icon>
            {targetName}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      {/* ---- Left pane: schedule list ---- */}
      <div className={clsx(classes.pane, classes.listPane)}>
        <div className={classes.paneHeader}>
          <div className={classes.paneTitle}>
            {schedules.length} schedule{schedules.length === 1 ? '' : 's'}
          </div>
          <Tooltip title="Add schedule">
            <IconButton onClick={handleAdd}>
              <Icon>add</Icon>
            </IconButton>
          </Tooltip>
        </div>
        {schedules.length === 0 ? (
          <div className={classes.listEmpty}>
            No schedules yet. Click + to add one.
          </div>
        ) : (
          schedules.map(renderListItem)
        )}
      </div>

      {/* ---- Middle pane: editor (Tasks detail pattern) ---- */}
      <div className={clsx(classes.pane, classes.editorPane)}>
        {!draft ? (
          <div className={classes.editorEmpty}>
            <Icon className={classes.editorEmptyIcon}>autorenew</Icon>
            <div className={classes.editorEmptyText}>
              {schedules.length === 0
                ? 'This template has no schedules. Click the + in the left pane to add one.'
                : 'Select a schedule from the left to edit it.'}
            </div>
          </div>
        ) : (
          <div className={classes.editorBody}>
            {/* Inline-editable schedule name (click to edit, blur/Enter to
             *  commit). Action buttons live in the preview pane (right). */}
            <div className={classes.editorTitleRow}>
              {titleEdit ? (
                <div className={classes.editorTitle}>
                  <TextField
                    autoFocus
                    value={titleDraft}
                    onChange={(e: any) => setTitleDraft(e.target.value)}
                    onBlur={() => {
                      patchDraft({ name: titleDraft || undefined });
                      setTitleEdit(false);
                    }}
                    onKeyDown={(e: any) => {
                      if (e.key === 'Enter') {
                        patchDraft({ name: titleDraft || undefined });
                        setTitleEdit(false);
                      } else if (e.key === 'Escape') {
                        setTitleDraft(draft.name ?? '');
                        setTitleEdit(false);
                      }
                    }}
                    placeholder={summarizeSchedule(draft)}
                    fullWidth
                    slotProps={{
                      input: {
                        style: { fontSize: fonts.xl, fontWeight: 500 },
                      },
                    }}
                  />
                </div>
              ) : (
                <div
                  className={classes.editorTitle}
                  onClick={() => setTitleEdit(true)}
                  title="Click to rename"
                >
                  {draft.name ?? summarizeSchedule(draft)}
                </div>
              )}
            </div>

            {supersededByNames.length > 0 && (
              <div style={{ padding: '0 1.5rem 0.75rem 4.25rem' }}>
                <div className={classes.supersededBadge}>
                  <Icon style={{ fontSize: '1rem' }}>arrow_upward</Icon>
                  Covered by: {supersededByNames.join(', ')}
                </div>
              </div>
            )}

            {/* Active + Lead time in a single time-style row */}
            <div className={classes.fieldRow}>
              <Icon className={classes.fieldRowIcon}>tune</Icon>
              <div className={classes.fieldRowContent}>
                <div className={classes.fieldGrid}>
                  <div className={classes.fieldGridItem}>
                    <div className={classes.fieldStaticLabel}>Active</div>
                    <Switch
                      checked={draft.active}
                      onChange={(_e: any, checked: boolean) =>
                        patchDraft({ active: checked })
                      }
                    />
                  </div>
                  <div className={classes.fieldGridItem} style={{ width: '9rem' }}>
                    <TextField
                      label="Lead time (days)"
                      type="number"
                      value={draft.leadTimeDays}
                      onChange={(e: any) =>
                        patchDraft({ leadTimeDays: Number(e.target.value) })
                      }
                      fullWidth
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Target folder */}
            <div className={classes.fieldRow}>
              <Icon
                className={classes.fieldRowIcon}
                style={{ color: folderColor(targetCategory) ?? undefined }}
              >
                folder
              </Icon>
              <div className={classes.fieldRowContent}>
                <div className={classes.fieldStaticLabel}>Target folder</div>
                <div className={classes.folderPickerRow}>
                  {targetCategory ? (
                    <span className={classes.folderPickerPath}>
                      {formatPath(targetCategory.path) || targetCategory.name}
                    </span>
                  ) : (
                    <span className={classes.folderPickerEmpty}>
                      No folder selected
                    </span>
                  )}
                  <Button
                    size="small"
                    onClick={() => {
                      setFolderPickerDraft(draft.targetCategoryId);
                      setFolderPickerOpen(true);
                    }}
                  >
                    {draft.targetCategoryId ? 'Change' : 'Select'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Trigger section (collapsible like Tasks Forms section) */}
            <div className={classes.section}>
              <div
                className={classes.sectionHeader}
                onClick={() => setTriggerOpen(o => !o)}
                style={{ cursor: 'pointer' }}
              >
                <Icon className={classes.sectionIcon}>flash_on</Icon>
                <div className={classes.sectionTitle}>Trigger</div>
                <div className={classes.sectionActions}>
                  <IconButton onClick={(e: any) => {
                    e.stopPropagation();
                    setTriggerOpen(o => !o);
                  }}>
                    <Icon>{triggerOpen ? 'expand_less' : 'expand_more'}</Icon>
                  </IconButton>
                </div>
              </div>
              {triggerOpen && (
                <div className={classes.sectionBody}>
                  <TriggerBuilder
                    trigger={draft.trigger}
                    schema={schemaForTarget}
                    onChange={t => patchDraft({ trigger: t })}
                  />
                </div>
              )}
            </div>

            {/* Covers section (collapsible) */}
            {otherTemplates.length > 0 && (
              <div className={classes.section}>
                <div
                  className={classes.sectionHeader}
                  onClick={() => setCoversOpen(o => !o)}
                  style={{ cursor: 'pointer' }}
                >
                  <Icon className={classes.sectionIcon}>layers</Icon>
                  <div className={classes.sectionTitle}>Covers</div>
                  <div className={classes.sectionCount}>
                    {(draft.supersedes ?? []).length}
                  </div>
                  <div className={classes.sectionActions}>
                    <IconButton onClick={(e: any) => {
                      e.stopPropagation();
                      setCoversOpen(o => !o);
                    }}>
                      <Icon>{coversOpen ? 'expand_less' : 'expand_more'}</Icon>
                    </IconButton>
                  </div>
                </div>
                {coversOpen && (
                  <div className={classes.sectionBody}>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={otherTemplates}
                      value={otherTemplates.filter(t =>
                        (draft.supersedes ?? []).includes(t.id),
                      )}
                      onChange={(_e: any, value: any) =>
                        patchDraft({
                          supersedes: Array.isArray(value)
                            ? value.map((v: any) => v.id)
                            : [],
                        })
                      }
                      getOptionLabel={(opt: any) => opt?.name ?? ''}
                      placeholder={
                        (draft.supersedes ?? []).length === 0
                          ? 'Search templates to cover…'
                          : ''
                      }
                      noOptionsText="No matching templates"
                    />
                    {(draft.supersedes ?? []).length > 0 && (
                      <div className={classes.coverageWindow}>
                        <span>Skip a covered schedule firing within</span>
                        <div style={{ width: '4rem' }}>
                          <TextField
                            type="number"
                            value={draft.supersedesWindowDays ?? 7}
                            onChange={(e: any) =>
                              patchDraft({
                                supersedesWindowDays: Math.max(
                                  0,
                                  Number(e.target.value) || 0,
                                ),
                              })
                            }
                          />
                        </div>
                        <span>
                          day{(draft.supersedesWindowDays ?? 7) === 1 ? '' : 's'}.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---- Right pane: action toolbar (no borders) + preview ---- */}
      <div className={clsx(classes.pane, classes.previewPane)}>
        {draft && (
          <div className={classes.previewActions}>
            <Tooltip title="Save schedule">
              <IconButton onClick={handleSave}>
                <Icon>save</Icon>
              </IconButton>
            </Tooltip>
            <Tooltip title="Generate work orders now">
              <IconButton onClick={handleGenerate}>
                <Icon>flash_on</Icon>
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete schedule">
              <IconButton onClick={handleDelete}>
                <Icon>delete</Icon>
              </IconButton>
            </Tooltip>
          </div>
        )}
        {draft ? (
          <SchedulePreview
            templateId={workTemplate.id}
            scheduleId={draft.id ?? undefined}
            refreshToken={refreshToken}
          />
        ) : (
          <div className={classes.editorEmpty}>
            <div className={classes.editorEmptyText}>
              Select a schedule to see its preview.
            </div>
          </div>
        )}
      </div>

      <Modal
        title="Select target folder"
        open={folderPickerOpen}
        onCancel={() => setFolderPickerOpen(false)}
        onConfirm={() => {
          patchDraft({ targetCategoryId: folderPickerDraft });
          setFolderPickerOpen(false);
        }}
        confirmButtonText="Select"
        disabled={!folderPickerDraft}
        contentStyle={{ height: '60vh', padding: 0 }}
      >
        <InventoryAssetTree
          includeAssets={false}
          selectedCategoryIds={new Set([folderPickerDraft])}
          onCategoryToggle={(c: any) => {
            setFolderPickerDraft(prev => (prev === c.id ? '' : c.id));
          }}
          includeLocationBased
        />
      </Modal>
    </div>
  );
};

export default ScheduleTab;
